import { Link } from "react-router-dom";
import { Timer, Copy, Trash2, Trophy } from "lucide-react";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { WeatherIcon } from "@/components/WeatherIcon";
import { dirOf } from "@/components/ConditionsFields";
import { useT } from "@/lib/i18n";
import { useSettings } from "@/context/SettingsContext";
import { TYRES, gearRatio, pressureDelta } from "@/lib/format";
import { fmtLap, lapStats } from "@/lib/laps";
import { STATUS_CLS, analyzeStint, findTarget, fmtP, stintsOf, tyreLabel } from "@/lib/pressureTargets";

const PressureBadge = ({ session, stint }) => {
  const { t } = useT();
  const targets = useSettings().settings.tyre_targets;
  const st = stintsOf(session)[stint - 1];
  if (!st) return null;
  const target = findTarget(targets, st.brand, st.model);
  const label = tyreLabel(st.brand, st.model);
  if (!label) return null;
  const a = analyzeStint(st, target);
  const unit = st.unit;
  return (
    <div data-testid={`session-card-pressure-${stint}`} className="flex flex-wrap items-center gap-2 mb-1.5 font-mono text-[11px]">
      <span className="text-slate-400">{label}</span>
      {target ? (
        <>
          <span className="text-slate-500">→ {fmtP(target.target, unit)}</span>
          <span data-testid={`session-card-pressure-status-${stint}`} className={`px-1.5 py-0.5 border rounded-sm uppercase text-[10px] ${STATUS_CLS[a.overall]}`}>
            {a.overall === "na" ? t("p_na_hot") : a.overall === "ok" ? t("p_ok") : `${t(`p_${a.overall}`)} max ${fmtP(a.worst, unit, true)}`}
          </span>
        </>
      ) : <span className="text-[#FFB800]">{t("no_target_short")}</span>}
    </div>
  );
};

const theoOf = (ld) => (ld?.laps?.length ? lapStats(ld).theoretical : null);

const Theoretical = ({ session, t }) => {
  const vals = [[session.lap_data, 1], [session.pit ? session.lap_data2 : null, 2]]
    .map(([ld, k]) => [theoOf(ld), ld?.precision, k]).filter(([v]) => v != null);
  if (!vals.length) return null;
  return (
    <div data-testid="session-card-theoretical" className="font-mono text-xs text-[#A855F7] mt-0.5">
      <span className="text-[10px] uppercase">{t("theoretical")}</span>{" "}
      {vals.map(([v, p, k]) => <b key={k} className="ml-1">{session.pit && <span className="text-[10px] text-slate-500 mr-0.5">S{k}</span>}{fmtLap(v, p ?? 3)}</b>)}
    </div>
  );
};

const TyreMini = ({ pos, v, unit }) => {
  const { t } = useT();
  const d = pressureDelta(v?.cold, v?.hot);
  return (
    <div className="bg-[#090A0F] border border-[#282D3D] rounded-sm px-2 py-1.5">
      <div className="font-mono text-[9px] uppercase text-slate-500">{t(`tyre_${pos}`)}</div>
      <div className="font-mono text-xs">
        <span className="text-[#007AFF]">{v?.cold ?? "—"}</span>
        <span className="text-slate-600"> → </span>
        <span className="text-[#FF3B30]">{v?.hot ?? "—"}</span>
      </div>
      {d != null && <div className="font-mono text-[10px] text-[#FFB800]">{d > 0 ? "+" : ""}{d.toFixed(unit === "psi" ? 1 : 2)}</div>}
    </div>
  );
};

const ConditionsLine = ({ c }) => {
  const { t } = useT();
  if (!c || (!c.weather && c.air_temp == null && c.track_temp == null && c.humidity == null && c.wind_speed == null && c.pressure == null)) return null;
  return (
    <div data-testid="session-card-conditions" className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 font-mono text-xs text-slate-300">
      {c.weather && <span className="flex items-center gap-1.5 text-[#FFB800]"><WeatherIcon weather={c.weather} className="w-3.5 h-3.5" />{t(`weather_${c.weather}`)}</span>}
      {c.air_temp != null && <span><span className="text-slate-500">{t("air_short")} </span>{c.air_temp}°C</span>}
      {c.track_temp != null && <span><span className="text-slate-500">{t("track_short")} </span>{c.track_temp}°C</span>}
      {c.humidity != null && <span data-testid="session-card-humidity"><span className="text-slate-500">{t("hum_short")} </span>{c.humidity}%</span>}
      {c.wind_speed != null && <span data-testid="session-card-wind"><span className="text-slate-500">{t("wind_short")} </span>{c.wind_speed} km/h{c.wind_deg != null ? ` ${dirOf(c.wind_deg)}` : ""}</span>}
      {c.pressure != null && <span data-testid="session-card-pressure">{c.pressure} hPa</span>}
    </div>
  );
};

const StintInfo = ({ session, stint, tyreSets }) => {
  const { t } = useT();
  const setId = stint === 1 ? session.tyre_set_id : session.tyre_set2_id;
  const laps = stint === 1 ? session.laps : session.laps2;
  const set = tyreSets.find((s) => s.id === setId);
  if (!set && laps == null) return null;
  return (
    <div data-testid={`session-card-stint${stint}-info`} className="flex flex-wrap gap-x-3 font-mono text-[11px] mb-1">
      {set && <span className="text-slate-300"><span className="text-slate-500">{t("tyre_set")}: </span>{set.name}</span>}
      {laps != null && <span className="text-slate-300">{laps} {t("laps_short")}</span>}
    </div>
  );
};

export const SessionCard = ({ session, eventId, isBest, garage, tyreSets = [], onDuplicate, onDelete, index }) => {
  const hideCat = useSettings().settings.hide_categories;
  const { t } = useT();
  const chassis = garage.chassis.find((c) => c.id === session.chassis_id);
  const engine = garage.engines.find((e) => e.id === session.engine_id);
  const ratio = gearRatio(session.gear?.sprocket, session.gear?.pinion);
  return (
    <div data-testid={`session-card-${session.id}`} style={{ animationDelay: `${index * 50}ms` }}
      className={`fade-up relative bg-[#12141C] border rounded-sm p-5 flex flex-col gap-4 hover:bg-[#181B26] transition-colors duration-200 ${isBest ? "border-[#10B981]/60" : "border-[#282D3D]"}`}>
      {isBest && (
        <span data-testid="session-best-badge" className="absolute -top-2.5 right-4 bg-[#10B981] text-[#090A0F] font-mono text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm flex items-center gap-1">
          <Trophy className="w-3 h-3" /> {t("event_best")}
        </span>
      )}
      <Link to={`/events/${eventId}/sessions/${session.id}`} data-testid={`session-open-${session.id}`} className="block">
        <div className="flex justify-between items-start gap-3">
          <div className="min-w-0">
            <h3 data-testid="session-card-name" className="font-heading font-bold uppercase text-2xl leading-tight">{session.name}</h3>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {session.driver && <span data-testid="session-card-driver" className="font-heading font-semibold text-lg text-[#FF3B30] leading-none">{session.driver}</span>}
              {!hideCat && session.category && <span data-testid="session-card-category" className="font-mono text-[10px] uppercase border border-[#FFB800]/40 text-[#FFB800] rounded-sm px-1.5 py-0.5">{session.category}</span>}
              {session.session_date && <span data-testid="session-card-datetime" className="font-mono text-[10px] text-slate-400">{session.session_date.split("-").reverse().join("/")}{session.session_time ? ` · ${session.session_time}` : ""}</span>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="flex items-center gap-1 justify-end font-mono text-[10px] uppercase text-slate-500"><Timer className="w-3 h-3" /> {t("best_lap")}</div>
            <div data-testid="session-card-best-lap" className={`font-mono font-bold text-xl ${isBest ? "text-[#10B981]" : "text-white"}`}>
              {session.pit && <span className="text-[10px] text-slate-500 mr-1">S1</span>}{session.best_lap || "—"}
            </div>
            {session.pit && (
              <div data-testid="session-card-best-lap2" className={`font-mono font-bold text-xl ${isBest ? "text-[#10B981]" : "text-white"}`}>
                <span className="text-[10px] text-slate-500 mr-1">S2</span>{session.best_lap2 || "—"}
              </div>
            )}
            <Theoretical session={session} t={t} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3 font-mono text-xs">
          <div><div className="text-[10px] uppercase text-slate-500">{t("chassis")}</div><div className="truncate">{chassis?.name || "—"}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500">{t("engine")}</div><div className="truncate">{engine?.name || "—"}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500">{t("ratio")}</div><div>{session.gear?.pinion && session.gear?.sprocket ? `${session.gear.pinion}/${session.gear.sprocket}` : "—"}{ratio ? ` (${ratio})` : ""}</div></div>
        </div>
        <ConditionsLine c={session.conditions} />
        {(session.pit ? [["tyres", "S1"], ["tyres2", "S2"]] : [["tyres", null]]).map(([key, label]) => (
          <div key={key} data-testid={`session-card-${key}`} className="mt-3">
            {label && <div className="font-mono text-[10px] uppercase text-[#FFB800] mb-1">PIT · {label}</div>}
            <StintInfo session={session} stint={key === "tyres" ? 1 : 2} tyreSets={tyreSets} />
            <PressureBadge session={session} stint={key === "tyres" ? 1 : 2} />
            <div className="grid grid-cols-2 gap-1.5">
              {TYRES.map((p) => <TyreMini key={p} pos={p} v={session[key]?.[p]} unit={session.pressure_unit} />)}
            </div>
          </div>
        ))}
        {session.notes && <p className="text-sm text-slate-400 mt-3 line-clamp-2">{session.notes}</p>}
      </Link>
      <div className="flex justify-end gap-1 border-t border-[#282D3D] pt-3 mt-auto">
        <button data-testid={`session-duplicate-${session.id}`} onClick={() => onDuplicate(session)} title={t("duplicate")}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-mono uppercase text-slate-400 hover:text-white hover:bg-[#282D3D] rounded-sm transition-colors duration-200">
          <Copy className="w-3.5 h-3.5" /> {t("duplicate")}
        </button>
        <ConfirmDelete testid={`session-delete-${session.id}`} onConfirm={() => onDelete(session)} trigger={
          <button data-testid={`session-delete-${session.id}-btn`} title={t("delete")}
            className="flex items-center px-2.5 py-1.5 text-slate-500 hover:text-[#FF3B30] hover:bg-[#282D3D] rounded-sm transition-colors duration-200">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        } />
      </div>
    </div>
  );
};
